import {
  ApnWithSig,
  AuthenticatedPrescriptionRequest,
  ApprovedPrescription,
  ApprovePrescriptionRequest,
  CancelEScriptRequest,
  CancelScriptRequest,
  CheckoutLink,
  DownloadLink,
  ElectronicsAddressSource,
  PrescriptionRejectionRequest,
  PrescriptionRequest,
  TokenResponse,
} from '@/lib/apis/types'
import {
  ApprovePrescriptionUrl,
  CancelPrescriptionUrl,
  DownloadPrescriptionPdfUrl,
  GetScriptDetailsUrl,
  RejectPrescriptionUrl,
  VerifyHpiiForPrescriptionUrl,
  VerifyHpioForPrescriptionUrl,
  VerifyIHIForPrescriptionUrl,
} from '@/lib/apis/routes'
import { MutationKeys, QueryKeys } from '@/lib/apis/service'
import { QUERY_STALE_TIME } from '@/lib/constants'
import { ApiClient } from '@/lib/apis/clients/ApiClient'
import WebRequest from '@/lib/apis/WebRequest'
import { useQuery } from '@tanstack/react-query'

export namespace CreatePrescriptionRequest {
  type Request = PrescriptionRequest | AuthenticatedPrescriptionRequest

  function api(data: Request): WebRequest {
    return WebRequest.POST('/prescriptions').body(data)
  }

  export function mutation(client: ApiClient) {
    return {
      mutationFn: (data: Request) => client.execute<CheckoutLink>(api(data)),
      mutationKey: MutationKeys.RequestPrescription(),
    }
  }
}

export namespace VerifyIHIForPrescription {
  type Params = {
    reference: string
  }

  function api({ reference }: Params): WebRequest {
    return WebRequest.POST(VerifyIHIForPrescriptionUrl(reference))
  }

  export function mutation(client: ApiClient, reference: string) {
    return {
      mutationFn: (params: Params) =>
        client.execute<TokenResponse>(api(params)),
      mutationKey: MutationKeys.VerifyIHIForPrescription(reference),
    }
  }
}

export namespace VerifyHpiiForPrescription {
  type Params = {
    reference: string
  }

  function api({ reference }: Params): WebRequest {
    return WebRequest.POST(VerifyHpiiForPrescriptionUrl(reference))
  }

  export function mutation(client: ApiClient, reference: string) {
    return {
      mutationFn: (params: Params) =>
        client.execute<TokenResponse>(api(params)),
      mutationKey: MutationKeys.VerifyHpiiForPrescription(reference),
    }
  }
}

export namespace VerifyHpioForPrescription {
  type Params = {
    reference: string
  }

  function api({ reference }: Params): WebRequest {
    return WebRequest.POST(VerifyHpioForPrescriptionUrl(reference))
  }

  export function mutation(client: ApiClient, reference: string) {
    return {
      mutationFn: (params: Params) =>
        client.execute<TokenResponse>(api(params)),
      mutationKey: MutationKeys.VerifyHpioForPrescription(reference),
    }
  }
}

export namespace ApprovePrescription {
  type Params = {
    request: ApprovePrescriptionRequest
    reference: string
  }

  function api({ request, reference }: Params): WebRequest {
    return WebRequest.POST(ApprovePrescriptionUrl(reference)).body(request)
  }

  export function mutation(client: ApiClient, params: Omit<Params, 'request'>) {
    const { reference } = params

    return {
      mutationFn: (request: ApprovePrescriptionRequest) =>
        client.executeEmpty(api({ ...params, request })),
      mutationKey: MutationKeys.ApprovePrescription(reference),
    }
  }
}

export namespace AbortPrescription {
  type Params = {
    clientId: string
  }

  function api({ clientId }: Params): WebRequest {
    return WebRequest.POST(
      `/doctor/prescriptions/abort-prescription?clientId=${clientId}`,
    )
  }

  export function mutation(client: ApiClient) {
    return {
      mutationFn: (clientId: string) => client.executeEmpty(api({ clientId })),
      mutationKey: MutationKeys.AbortPrescription(),
    }
  }
}

export namespace PrescribeMedicine {
  async function api<T, R>(
    client: ApiClient,
    url: string,
    body: T,
  ): Promise<R> {
    const request = WebRequest.POST(url).body(body)
    return client.execute<R>(request)
  }

  export function mutation<T, R = ApprovedPrescription>(
    client: ApiClient,
    url: string,
    reference: string,
  ) {
    return {
      mutationFn: (body: T) => api<T, R>(client, url, body),
      mutationKey: MutationKeys.PrescribeMedicine(reference),
    }
  }
}

export namespace RejectPrescription {
  type Params = {
    request: PrescriptionRejectionRequest
    reference: string
  }

  function api({ request, reference }: Params): WebRequest {
    return WebRequest.POST(RejectPrescriptionUrl(reference)).body(request)
  }

  export function mutation(client: ApiClient, params: Omit<Params, 'request'>) {
    const { reference } = params

    return {
      mutationFn: (request: PrescriptionRejectionRequest) =>
        client.executeEmpty(api({ ...params, request })),
      mutationKey: MutationKeys.RejectPrescription(reference),
    }
  }
}

export namespace DownloadPrescriptionPdf {
  type Params = {
    reference: string
    scriptId: string
  }

  function api({ reference, scriptId }: Params): WebRequest {
    return WebRequest.GET(DownloadPrescriptionPdfUrl(reference, scriptId))
  }

  export function mutation(client: ApiClient) {
    return {
      mutationFn: async (params: Params) => {
        const { reference, scriptId } = params
        if (!reference || !scriptId) return null

        return await client.execute<DownloadLink | null>(api(params))
      },
      mutationKey: MutationKeys.DownloadPrescriptionPDF(),
    }
  }
}

export namespace CancelPaperPrescription {
  type Params = {
    reference: string
    scriptId: string
  }

  function api(
    { reference, scriptId }: Params,
    body: CancelScriptRequest,
  ): WebRequest {
    return WebRequest.POST(CancelPrescriptionUrl(reference, scriptId)).body(
      body,
    )
  }

  export function mutation(client: ApiClient, params: Params) {
    return {
      mutationFn: async (body: CancelScriptRequest) => {
        if (!params.reference || !params.scriptId) return null
        return await client.executeEmpty(api(params, body))
      },
      mutationKey: MutationKeys.CancelPrescription(
        params.reference,
        params.scriptId,
      ),
    }
  }
}

export namespace CancelElectronicsPrescription {
  type Params = {
    reference: string
    scriptId: string
  }

  function api(
    { reference, scriptId }: Params,
    body: CancelEScriptRequest,
  ): WebRequest {
    return WebRequest.POST(
      `/doctor/prescriptions/${reference}/${scriptId}/cancel-e-script`,
    ).body(body)
  }

  export function mutation(client: ApiClient, params: Params) {
    return {
      mutationFn: async (body: CancelEScriptRequest) =>
        await client.executeEmpty(api(params, body)),
      mutationKey: MutationKeys.CancelEScript(
        params.reference,
        params.scriptId,
      ),
    }
  }
}

export namespace GetScriptDetails {
  type Params = {
    reference: string
    scriptId: string
  }

  function api({ reference, scriptId }: Params): WebRequest {
    return WebRequest.GET(GetScriptDetailsUrl(reference, scriptId))
  }

  export function query(client: ApiClient, params: Params) {
    return {
      queryFn: async () =>
        await client.execute<ApprovedPrescription>(api(params)),
      queryKey: QueryKeys.ScriptDetails(params.reference, params.scriptId),
    }
  }

  export function useApiQuery(
    client: ApiClient,
    params: Params,
    enabled: boolean,
  ) {
    return useQuery({
      ...query(client, params),
      refetchInterval: (q) => {
        if (
          q.state.data?.erxCreationStatus === 'SCHEDULED' ||
          q.state.data?.erxCancellationStatus === 'SCHEDULED' ||
          !(q.state.data?.processed ?? false)
        )
          return QUERY_STALE_TIME
        else return false
      },
      enabled,
      staleTime: Infinity,
    })
  }
}

export namespace GetApn {
  export function query(client: ApiClient) {
    return {
      queryFn: async () => {
        const req = WebRequest.GET('/profile/apn')
        return client.execute<ApnWithSig>(req)
      },
      queryKey: QueryKeys.GetApn(),
    }
  }
}

export namespace ReissueEScriptToken {
  type Params = {
    reference: string
    scriptId: string
    body: ElectronicsAddressSource
  }

  function api({ reference, scriptId, body }: Params): WebRequest {
    return WebRequest.POST(
      `/doctor/prescriptions/${reference}/${scriptId}/reissue-token`,
    ).body(body)
  }

  export function mutation(client: ApiClient) {
    return {
      mutationFn: async (params: Params) =>
        await client.executeEmpty(api(params)),
      mutationKey: MutationKeys.ReissueEScriptToken(),
    }
  }
}
